/**
 * Full-name search for CLIENT-SIDE list filters (card CRM-FULLNAME-SEARCH).
 *
 * The browser twin of database/nameSearchSql.js — same tokenizer, same
 * all-tokens-must-appear rule, so a staff member who types "Joe Bubba" gets
 * the same answer whether the tab filters server-side (crm:filtered-list) or
 * in JS over an already-loaded array.
 *
 * ⚠ It is a SEPARATE FILE rather than a shared import on purpose: CRA cannot
 * import from outside src/, so database/nameSearchSql.js is unreachable here.
 * The two are kept in step by tests/unit/crm/nameSearch.test.js, which asserts
 * the same matrix against both.
 *
 * Why this exists at all: the old filters were `${first} ${last}`.includes(q),
 * which finds "Joe Bubba" but not "Bubba Joe", not "Joe  Bubba" with a double
 * space, and not "Bubba, Joe" — and the ones that tested first_name and
 * last_name separately could not find any two-part name whatsoever.
 */

const MAX_TOKENS = 10;

/**
 * Split a user's search box input into match tokens.
 * Mirrors tokenizeNameQuery in database/nameSearchSql.js exactly.
 */
export function tokenizeNameQuery(query) {
  if (typeof query !== 'string') return [];
  return query.split(/[\s,]+/).filter(Boolean).slice(0, MAX_TOKENS);
}

/**
 * True when EVERY token in `query` appears somewhere in `parts`, in any order,
 * case-insensitively.
 *
 * An empty / whitespace-only query matches everything, so a call site can use
 * this as its whole predicate without a separate "no filter" guard.
 *
 * @param {Array<string|null|undefined>} parts  name fields, e.g. [c.first_name, c.last_name, c.email]
 * @param {string} query
 * @returns {boolean}
 */
export function matchesNameQuery(parts, query) {
  const tokens = tokenizeNameQuery(query);
  if (tokens.length === 0) return true;
  // Joined with a space, the same separator the SQL haystack uses. A token
  // never contains whitespace, so it can never match across the boundary —
  // which is what keeps single-token behaviour identical to the old
  // per-field `.includes()`.
  const hay = parts.filter(Boolean).join(' ').toLowerCase();
  return tokens.every(t => hay.includes(t.toLowerCase()));
}
